import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { bool } from 'prop-types';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import Icon from 'yoda-core-components/lib/components/Icon/next';
import Button from 'yoda-core-components/lib/components/Button';
import IconTriangle from '../../assets/svg/Triangle.svg';
import ImageBanner from '../CMImageBanner/ImageBanner';
import { plccApplyAnalytics, plccViewsAnalytics } from '../../actions/AnalyticsAction';
import { metaDataElement, metaDataProperty } from '../../utils/cmPreviewMetaData';
import {
    commonAutomationIds,
    ctaAutomationIds,
    heroAutomationIds,
} from '../CoreMediaPage/CMPage.config';

const Hero = (props) => {
    const data = get(props, 'data', props);
    const [messageGraphicImageHeight, setMessageGraphicImageHeight] = useState(0);
    let wrapperRef = useRef(null);
    let messageLockUpTextRef = useRef(null);
    const { isCMPreview } = props;
    const { automationId } = data;
    const isBgColorAvailable = get(data, 'styles.componentContainerBackgroundColor', '');
    const containerColor = !isBgColorAvailable ? '' : `#${isBgColorAvailable}`;
    const imageGraphicAvailable = get(data, 'imageGraphic', {});
    const messageGraphicAvailable = get(data, 'messageGraphic', {});
    const eyeBrowText = get(data, 'eyeBrowText', '');
    const styles = get(data, 'styles', {});
    const teaserTitle = get(data, 'teaserTitle', '');
    const teaserSubtitle = get(data, 'teaserSubtitle', '');
    const legalTextData = get(data, 'legalText', {});
    const plccAnalyticsTagName = get(data, 'preferences.plccAnalyticsTagName', 'PLCC');
    const plccBenefitPageURL = get(data, 'preferences.plccBenefitsUrl', '/m/plcc-benefits');
    const { analyticsId = '' } = legalTextData;
    const ctaData = get(data, 'ctaLinks', []);
    const pictureVariantsData = data?.backGround ? [...data?.backGround] : [];
    const isDesktop = get(props, 'deviceType.isDesktop', false);

    const componentStylingResp = {
        lockUpPaddingSize: {
            S: [!isBgColorAvailable && 'lg:w-[calc(35%-24px)]'],
            M: [!isBgColorAvailable && 'lg:w-[calc(35%-48px)]'],
            L: [!isBgColorAvailable && 'lg:w-[calc(35%-50px)]'],
        },
        padding: {
            S: `${dt(['lg:m-6', 'smMd:m-6'])}`,
            M: `${dt(['lg:m-12', 'smMd:m-6'])}`,
            L: `${dt(['lg:m-20', 'smMd:m-6'])}`,
            None: `${dt(['m-0'])}`,
        },
        fontSize: {
            'heading-1': `${dt(['heading-1'])}`,
            'heading-2': `${dt(['heading-2'])}`,
            'heading-3': `${dt(['heading-3'])}`,
            'heading-4': `${dt(['heading-4'])}`,
            'heading-5': `${dt(['heading-5'])}`,
            'heading-6': `${dt(['heading-6'])}`,
        },
        subTitleFontSize: {
            'body-1': `${dt(['text-normal'])}`,
            'body-2': `${dt(['text-xlarge'])}`,
        },
        eyeBrowTextFontSize: {
            'heading-1': `${dt(['heading-1'])}`,
            'heading-2': `${dt(['heading-2'])}`,
            'heading-3': `${dt(['heading-3', 'smMd:leading-13', 'lg:leading-15'])}`,
            'heading-4': `${dt(['heading-4'])}`,
            'heading-5': `${dt(['heading-5'])}`,
            'heading-6': `${dt(['heading-6'])}`,
        },
        ImageGraphicAlignment: {
            'top-right': `${dt(['top-0', 'right-0'])}`,
            'top-left': `${dt(['top-0', 'left-0'])}`,
            'bottom-right': `${dt(['bottom-0', 'right-0'])}`,
            'bottom-left': `${dt(['bottom-0', 'left-0'])}`,
        },
        ctaLinkAlignment: {
            'top-left': `${dt([
                'justify-start',
                'flex',
                '!font-semibold',
                'smMd:!text-base',
                'smMd:!leading-6',
                'last:mr-0',
            ])}`,
            'top-center': `${dt([
                'justify-center',
                'flex',
                '!font-semibold',
                'smMd:!text-base',
                'smMd:!leading-6',
                'last:mr-0',
            ])}`,
            'top-right': `${dt([
                'justify-end',
                'flex',
                '!font-semibold',
                'smMd:!text-base',
                'smMd:!leading-6',
                'last:mr-0',
            ])}`,
            'middle-left': `${dt([
                'justify-start',
                'flex',
                '!font-semibold',
                'smMd:!text-base',
                'smMd:!leading-6',
                'last:mr-0',
            ])}`,
            'middle-center': `${dt([
                'justify-center',
                'flex',
                '!font-semibold',
                'smMd:!text-base',
                'smMd:!leading-6',
                'last:mr-0',
            ])}`,
            'middle-right': `${dt([
                'justify-end',
                'flex',
                '!font-semibold',
                'smMd:!text-base',
                'smMd:!leading-6',
                'last:mr-0',
            ])}`,
            'bottom-left': `${dt([
                'justify-start',
                'flex',
                '!font-semibold',
                'smMd:!text-base',
                'smMd:!leading-6',
                'last:mr-0',
            ])}`,
            'bottom-center': `${dt([
                'justify-center',
                'flex',
                '!font-semibold',
                'smMd:!text-base',
                'smMd:!leading-6',
                'last:mr-0',
            ])}`,
            'bottom-right': `${dt([
                'justify-end',
                'flex',
                '!font-semibold',
                'smMd:!text-base',
                'smMd:!leading-6',
                'last:mr-0',
            ])}`,
        },
        componentMessageLockUpAlignment: {
            'top-left': `${dt(['top-0', 'left-0', 'text-left'])}`,
            'top-center': `${dt([
                'top-0',
                'left-1/2',
                '-translate-x-1/2',
                'justify-center',
                'text-center',
                'items-center',
            ])}`,
            'top-right': `${dt(['top-0', 'right-0', 'items-end', 'text-right'])}`,
            'middle-left': `${dt([
                'left-0',
                'justify-center',
                'items-start',
                'translate-y-1/2',
                'text-left',
                'bottom-1/2',
            ])}`,
            'middle-center': `${dt([
                'items-center',
                'justify-center',
                'left-1/2',
                '-translate-x-1/2',
                'translate-y-1/2',
                'text-center',
                'bottom-1/2',
            ])}`,
            'middle-right': `${dt([
                'justify-end',
                'items-end',
                'translate-y-1/2',
                'right-0',
                'text-right',
                'bottom-1/2',
            ])}`,
            'bottom-left': `${dt(['bottom-0', 'left-0', 'text-left'])}`,
            'bottom-center': `${dt([
                'bottom-0',
                'left-1/2',
                '-translate-x-1/2',
                'text-center',
                'justify-center',
                'items-center',
            ])}`,
            'bottom-right': `${dt(['bottom-0', 'right-0', 'items-end', 'text-right'])}`,
        },
        componentImagePosition: {
            'top-left': dt(['lg:object-left-top']),
            'top-center': dt(['lg:object-top']),
            'top-right': dt(['lg:object-right-top']),
            'middle-left': dt(['lg:object-left']),
            'middle-center': dt(['lg:object-center']),
            'middle-right': dt(['lg:object-right']),
            'bottom-left': dt(['lg:object-left-bottom']),
            'bottom-center': dt(['lg:object-bottom']),
            'bottom-right': dt(['lg:object-right-bottom']),
        },
        messageLockupCTAAlignment: {
            'top-center': `${dt(['justify-start'])}`,
            'top-center': `${dt(['justify-center'])}`,
            'top-right': `${dt(['justify-end'])}`,
            'middle-center': `${dt(['justify-start'])}`,
            'middle-center': `${dt(['justify-center'])}`,
            'middle-right': `${dt(['justify-end'])}`,
            'bottom-center': `${dt(['justify-start'])}`,
            'bottom-center': `${dt(['justify-center'])}`,
            'bottom-right': `${dt(['justify-end'])}`,
        },
        paddingConstants: {
            S: 48,
            M: isDesktop ? 96 : 48,
            L: isDesktop ? 160 : 48,
            None: 0,
        },
    };
    const ctaMiddleCenterAlignment =
        componentStylingResp?.messageLockupCTAAlignment[styles?.componentMessageLockUpAlignment];
    const ImageGraphicAlignment =
        componentStylingResp?.ImageGraphicAlignment[styles?.componentImageGraphicAlignment];

    const componentMessageLockUpAlignment =
        componentStylingResp?.componentMessageLockUpAlignment[
            styles?.componentMessageLockUpAlignment
        ];

    const componentCTALinkStyle =
        componentStylingResp?.ctaLinkAlignment[styles?.componentMessageLockUpAlignment];
    const componentCTABtnStyle = dt(['flex', 'w-60', 'last:mr-0']); // as per new mocks

    const heroComponentInsidePadding = !styles?.componentHeroInnerPadding
        ? 'm-6'
        : componentStylingResp?.padding[styles?.componentHeroInnerPadding];

    const heroComponentHeadlineFontSize =
        componentStylingResp?.fontSize[styles?.componentHeadlineFontSize];
    const heroComponentSubHeadlineFontSize =
        componentStylingResp?.subTitleFontSize[styles?.componentSubHeadlineFontSize];
    const heroComponentEyeBrowTextFontSize =
        componentStylingResp?.eyeBrowTextFontSize[styles?.componentEyeBrowFontSize];
    const dispatch = useDispatch();
    useEffect(() => {
        // get component styles from the props
        // store it in the
        if (analyticsId.toUpperCase().includes(plccAnalyticsTagName)) {
            const payloadParams = {
                plccBanner: 'static_rewards_apply',
            };
            dispatch(plccViewsAnalytics(payloadParams));
        }
    }, []);

    useEffect(() => {
        const heightOfMessageLockupFlexBox =
            messageLockUpTextRef && messageLockUpTextRef.clientHeight;

        if (heightOfMessageLockupFlexBox) {
            const insidePadding = styles?.componentHeroInnerPadding
                ? componentStylingResp?.paddingConstants[styles?.componentHeroInnerPadding]
                : 48;

            /* istanbul ignore next */
            if (wrapperRef)
                wrapperRef.style.minHeight = `${
                    heightOfMessageLockupFlexBox + messageGraphicImageHeight + insidePadding
                }px`;
        }
    }, [messageLockUpTextRef, messageGraphicImageHeight]);

    const imagePositionAlignment =
        componentStylingResp?.componentImagePosition[styles?.componentMessageLockUpAlignment];
    const BackGroundImg = () => {
        return (
            <ImageBanner
                pictureVariants={pictureVariantsData}
                imageStyles={dt(['lg:object-cover', 'lg:h-full', imagePositionAlignment])}
                imageContainerStyle={dt(['lg:h-full'])}
            />
        );
    };

    const ImageGraphic = () => {
        const { data, description } = imageGraphicAvailable;
        const imageGraphicSrc = `${data?.uri}`;
        const { data: metaData } = props;

        return (
            <div
                className="imageGraphic"
                data-cm-metadata={isCMPreview && metaDataElement(metaData, 'pictures')}
                data-automation-id={commonAutomationIds?.IMAGEGRAPHIC}
            >
                <img
                    src={imageGraphicSrc}
                    alt={description}
                    data-cm-metadata={isCMPreview && metaDataProperty('data')}
                />
            </div>
        );
    };

    const MessageGraphic = () => {
        const { data, description } = messageGraphicAvailable;
        const messageGraphicSrc = `${data?.uri}`;
        const { data: metaData } = props;

        return (
            <div
                className={dt(['flex', 'lg:pb-4', 'smMd:pb-2'])}
                data-cm-metadata={isCMPreview && metaDataElement(metaData, 'pictures')}
                data-automation-id={commonAutomationIds?.MESSAGEGRAPHIC}
            >
                <img
                    src={messageGraphicSrc}
                    alt={description}
                    data-cm-metadata={isCMPreview && metaDataProperty('data')}
                    onLoad={(event) => {
                        const styleOfParentEl = getComputedStyle(event.target.parentElement);
                        setMessageGraphicImageHeight(
                            event.target.clientHeight + parseInt(styleOfParentEl.paddingBottom)
                        );
                    }}
                />
            </div>
        );
    };

    const TeaserTitle = () => {
        return (
            <div
                className={`${heroComponentHeadlineFontSize} ${dt([
                    'font-open-sans-bold',
                    'text-black',
                ])}`}
                style={{ color: `#${styles?.componentHeadlineColor}` }}
                data-cm-metadata={isCMPreview && metaDataProperty('teaserTitle')}
                data-automation-id={commonAutomationIds?.MAINTITLE}
            >
                {teaserTitle}
            </div>
        );
    };

    const TeaserSubtitle = () => {
        return (
            <div
                className={`${heroComponentSubHeadlineFontSize} ${dt(['mt-2', 'text-black'])}`}
                style={{ color: `#${styles?.componentSubHeadlineColor}` }}
                data-cm-metadata={isCMPreview && metaDataProperty('teaserSubtitle')}
                data-automation-id={commonAutomationIds?.MAINSUBTITLE}
            >
                {teaserSubtitle}
            </div>
        );
    };

    const isPlccBenefitPage = !__SERVER__ && window.location.href.indexOf(plccBenefitPageURL) > -1;

    const isPlccEnableLink = () => {
        return analyticsId.toUpperCase().includes(plccAnalyticsTagName);
    };

    const onButtonClick = () => {
        if (isPlccEnableLink() && isPlccBenefitPage) {
            const payloadParams = {
                plccBanner: 'static_rewards_apply',
            };

            dispatch(plccApplyAnalytics(payloadParams));
        }
    };

    const LegalText = () => {
        const { value, openInNewTab } = legalTextData?.urlHolder;
        const linkTragetValue = openInNewTab ? '_blank' : '';
        const hasCTAComponent = !isEmpty(ctaData);
        return (
            <div
                className={dt(['text-xsmall', 'mt-2'])}
                data-automation-id={commonAutomationIds?.LEGALTEXT}
            >
                <span
                    style={{ color: `#${styles?.legalTextColor}` }}
                    id="legalText"
                    data-cm-metadata={isCMPreview && metaDataProperty('legalText.legalText')}
                >
                    {legalTextData?.legalText}
                    &nbsp;{' '}
                    <a
                        className={dt(['underline', 'text-gray-70', 'hover:text-gray-70'])}
                        aria-label={legalTextData?.legalLinkText}
                        target={linkTragetValue}
                        href={value}
                        alt={legalTextData?.legalAltText}
                        title={legalTextData?.legalAltText}
                        data-automation-id={commonAutomationIds?.LEGALLINK}
                        rel="noreferrer noopener"
                        style={{ color: `#${styles?.legalLinkColor}` }}
                    >
                        {legalTextData?.legalLinkText}
                    </a>
                </span>
            </div>
        );
    };

    const CtaComponet = () => {
        const isLink = styles?.componentCtaStyle === 'link';
        const isDropDown = styles?.componentCtaStyle === 'button' && ctaData?.length > 2;
        const isButton = styles?.componentCtaStyle !== 'link' && ctaData?.length <= 2;
        const dropbtnStyle = dt([
            'flex',
            'justify-center',
            'items-center',
            'bg-white',
            'text-black',
            'text-xLg',
            'border',
            'border-solid',
            'border-gray-40',
            'cursor-pointer',
            'w-60',
            'h-12',
            'py-2',
            'px-6',
            'rounded-lg',
            'whitespace-nowrap',
        ]);
        const dropdownContentStyle = dt([
            'hidden',
            'absolute',
            'bg-white',
            'w-60',
            'md:max-w-[248px]',
            'smOnly:max-w-[240px]',
            'shadow-elevation1',
            'group-hover:block',
            'text-center',
            'z-10',
        ]);
        const isCtaWhite =
            styles?.componentCtaColor === 'ffffff' ||
            styles?.componentCtaColor === 'fff' ||
            styles?.componentCtaColor === 'rgb(255, 255, 255)' ||
            styles?.componentCtaColor === 'rgba(255, 255, 255, 1)';
        const dropDownCTATextColor = isCtaWhite ? '' : `#${styles?.componentCtaColor}`;

        const ctaSmMdStyle = dt(['smMd:justify-center']);

        return (
            <div
                className={`${dt([
                    'flex',
                    'flex-wrap',
                    ctaMiddleCenterAlignment,
                    'sm:z-[3]',
                    'gap-x-4',
                    'mt-6',
                    isLink ? 'lg:mt-4' : '',
                    'gap-4',
                ])} ${!isBgColorAvailable && ctaSmMdStyle}`}
            >
                {isLink &&
                    ctaData?.map((currentCta) => (
                        <>
                            <Button
                                className={componentCTALinkStyle}
                                id="componentCTA"
                                aria-label={currentCta?.ctaText}
                                target={currentCta?.urlHolder?.openInNewTab ? '_blank' : ''}
                                linkUrl={currentCta?.urlHolder?.value}
                                alt={currentCta?.ctaAltText}
                                title={currentCta?.ctaAltText}
                                buttonType="Link"
                                size="Lg"
                                type="button"
                                automationId={ctaAutomationIds?.LINK}
                                style={{ color: `#${styles?.componentCtaColor}` }}
                            >
                                {currentCta?.ctaText}
                            </Button>
                        </>
                    ))}
                {isDropDown && (
                    <>
                        <div
                            className={dt(['relative', 'inline-block', 'group'])}
                            data-automation-id={ctaAutomationIds?.DROPDOWN}
                        >
                            <button
                                className={dropbtnStyle}
                                id="componentCTA_dropdown"
                                data-automation-id={ctaAutomationIds?.DROPDOWNBTN}
                                type="button"
                            >
                                <span className={dt(['font-open-sans-semibold', 'leading-7'])}>
                                    Shop All
                                </span>
                                <Icon
                                    iconType="svg"
                                    className={dt(['ml-2'])}
                                    width="16px"
                                    height="16px"
                                    viewBox="0 0 16 16"
                                >
                                    <IconTriangle />
                                </Icon>
                            </button>
                            <div
                                data-automation-id={ctaAutomationIds?.DROPDOWNITEMS}
                                className={dropdownContentStyle}
                            >
                                {ctaData?.map((currentCta) => (
                                    <a
                                        aria-label={currentCta?.ctaText}
                                        target={currentCta?.urlHolder?.openInNewTab ? '_blank' : ''}
                                        href={currentCta?.urlHolder?.value}
                                        alt={currentCta?.ctaAltText}
                                        title={currentCta?.ctaAltText}
                                        data-automation-id={ctaAutomationIds?.DROPDOWNLINK}
                                        rel="noreferrer noopener"
                                        style={{ color: dropDownCTATextColor }}
                                        className={dt([
                                            'text-black',
                                            'py-2.5',
                                            'px-6',
                                            'no-underline',
                                            'block',
                                            'hover:bg-light-white',
                                            'whitespace-nowrap',
                                        ])}
                                    >
                                        {currentCta?.ctaText}
                                    </a>
                                ))}
                            </div>
                        </div>
                    </>
                )}
                {isButton &&
                    ctaData?.map((currentCta) => (
                        <>
                            <Button
                                className={componentCTABtnStyle}
                                aria-label={currentCta?.ctaText}
                                target={currentCta?.urlHolder?.openInNewTab ? '_blank' : ''}
                                linkUrl={currentCta?.urlHolder?.value}
                                alt={currentCta?.ctaAltText}
                                title={currentCta?.ctaAltText}
                                id="componentCTA_button"
                                onClick={() => onButtonClick()}
                                automationId={ctaAutomationIds?.BUTTON}
                                buttonType="Secondary"
                                size="Lg"
                                type="button"
                            >
                                {currentCta?.ctaText}
                            </Button>
                        </>
                    ))}
            </div>
        );
    };
    const lockUpPaddingSize = get(
        componentStylingResp?.lockUpPaddingSize,
        `${styles.componentHeroInnerPadding}`,
        []
    );
    const bgImgSmMdStyles = dt([
        'smMd:!items-center',
        'smMd:!text-center',
        'smMd:bottom-0',
        'smMd:!transform-none',
    ]);
    return (
        <div
            className={dt([
                'relative',
                'block',
                'w-full',
                'h-full',
                'smOnly:!w-[calc(100%+48px)]',
                'smOnly:-mx-6',
                !isBgColorAvailable && 'lg:min-h-[424px]',
                !isBgColorAvailable && 'sm:min-h-[424px]',
                !isBgColorAvailable ? 'mdOnly:w-full' : 'mdOnly:!w-[calc(100%+48px)]',
                !isBgColorAvailable ? 'mdOnly:mx-0' : 'mdOnly:-mx-6',
            ])}
            style={{ backgroundColor: containerColor }}
            data-automation-id={automationId}
            data-cm-metadata={isCMPreview && metaDataElement(data)}
            ref={(el) => {
                if (el) {
                    wrapperRef = el;
                }
            }}
        >
            <div className={dt(['lg:h-full', 'relative'])}>
                {!isBgColorAvailable && <BackGroundImg />}
                <div
                    className={dt([
                        'absolute',
                        ImageGraphicAlignment,
                        heroComponentInsidePadding,
                        'z-[0]',
                    ])}
                >
                    {!isEmpty(imageGraphicAvailable) && <ImageGraphic />}
                </div>
            </div>
            <div
                data-automation-id="at-messageLock-container"
                className={`${dt([
                    heroComponentInsidePadding,
                    'hover:z-[3]',
                    isBgColorAvailable ? 'absolute' : 'lg:absolute',
                    'top-0',
                    'right-0',
                    'bottom-0',
                    'left-0',
                ])} ${!isBgColorAvailable && bgImgSmMdStyles}`}
            >
                <div
                    className={`messageLockupElement ${dt([
                        'w-full',
                        ...lockUpPaddingSize,
                        'flex',
                        'flex-col',
                        isBgColorAvailable ? 'absolute' : 'lg:absolute',
                        componentMessageLockUpAlignment,
                        'sm:z-[1]',
                    ])} ${!isBgColorAvailable && bgImgSmMdStyles}`}
                    data-automation-id={commonAutomationIds?.MESSAGECONTAINER}
                    style={{}}
                >
                    {!isEmpty(messageGraphicAvailable) && <MessageGraphic />}
                    <div
                        data-automation-id="at-messageLock-text-container"
                        className={dt(['w-full', 'break-words'])}
                        ref={(el) => {
                            if (el) {
                                messageLockUpTextRef = el;
                            }
                        }}
                    >
                        {!!eyeBrowText && (
                            <div
                                className={`${heroComponentEyeBrowTextFontSize} ${dt([
                                    'eyeBrowText',
                                    'font-open-sans-regular',
                                ])}`}
                                style={{ color: `#${styles?.componentEyeBrowTextColor}` }}
                                data-automation-id={heroAutomationIds?.EYEBROWTEXT}
                            >
                                {eyeBrowText}
                            </div>
                        )}
                        {!!teaserTitle && <TeaserTitle />}
                        {!!teaserSubtitle && <TeaserSubtitle />}
                        {!isEmpty(legalTextData) && <LegalText />}
                        {!isEmpty(ctaData) && <CtaComponet />}
                    </div>
                </div>
            </div>
        </div>
    );
};

Hero.defaultProps = {
    isCMPreview: false,
};

Hero.propTypes = {
    isCMPreview: bool,
};

export default Hero;
